import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import intakeApi from './intakeApi';
import usePageTitle from '../../hooks/usePageTitle';
import './intake.css';

const QUEUES = [
  { id: 'mine', label: 'My cases' },
  { id: 'global', label: 'Global queue' },
];

function money(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function when(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(); } catch { return '—'; }
}

export default function IntakeQueuePage() {
  usePageTitle('Intake');
  const navigate = useNavigate();
  const [queue, setQueue] = useState('mine');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    setError('');
    try {
      const data = await intakeApi.listCases({ queue: q });
      setItems(data?.data?.items || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load cases');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(queue); }, [queue, load]);

  return (
    <div className="intake-page">
      <div className="intake-page__head">
        <div>
          <h1 className="intake-page__title">Intake queue</h1>
          <p className="intake-page__subtitle">
            Cases you build from uploaded documents. Private cases are yours; move one
            to the global queue so any teammate can work on it.
          </p>
        </div>
        <button type="button" className="intake-btn" onClick={() => navigate('/intake/new')}>
          + New upload
        </button>
      </div>

      <div className="intake-tabs">
        {QUEUES.map((q) => (
          <button
            key={q.id}
            type="button"
            className={`intake-tab${queue === q.id ? ' is-active' : ''}`}
            onClick={() => setQueue(q.id)}
          >
            {q.label}
          </button>
        ))}
      </div>

      {error && <div className="intake-banner intake-banner--error">{error}</div>}

      {loading ? (
        <div className="intake-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="intake-empty">
          {queue === 'mine'
            ? 'No cases yet. Upload a set of documents to get started.'
            : 'The global queue is empty.'}
        </div>
      ) : (
        <table className="intake-table">
          <thead>
            <tr>
              <th>Case</th>
              <th>Payer</th>
              <th>Claim #</th>
              <th>Denial type</th>
              <th>Denied</th>
              <th>Queue</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.CaseId}>
                <td>
                  <Link className="intake-table__link" to={`/intake/${c.CaseId}`}>
                    {c.ExternalId || c.CaseId.slice(0, 8)}
                  </Link>
                </td>
                <td>{c.PayerName || '—'}</td>
                <td>{c.ClaimNumber || '—'}</td>
                <td>{c.Archetype || '—'}</td>
                <td>{money(c.TotalDeniedAmount)}</td>
                <td>
                  <span className={`intake-pill intake-pill--${c.Visibility || 'private'}`}>
                    {c.Visibility || 'private'}
                  </span>
                </td>
                <td>{c.Status || 'new'}</td>
                <td>{when(c.UpdatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
